import { CircleNotch } from "@phosphor-icons/react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getApiErrorMessage, useDeleteHaToken } from "@/lib/api";

interface Props {
  householdId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Confirmation before taking the hub token off a household.
 *
 * Saving an empty token field means "keep the one you have", so this is the
 * only way a wrong token comes off a record, and the copy says what goes and
 * what stays. Modelled on the Wi-Fi password dialog, which is the other secret
 * with its own delete.
 */
export function HaTokenDeleteDialog({ householdId, open, onOpenChange }: Props) {
  const deleteMutation = useDeleteHaToken();
  const [error, setError] = useState("");

  const handleDelete = async () => {
    setError("");
    try {
      await deleteMutation.mutateAsync({ id: householdId });
      onOpenChange(false);
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to remove the token."));
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setError("");
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Remove hub token</DialogTitle>
          <DialogDescription>
            Remove the stored token for this household? The hub address is kept, and nothing in the
            house changes. Until a new token is saved, this household reads as unreachable
            everywhere, and the resident cannot get past the SmartClimate Hub step. Make a new token
            on the hub itself, under profile, Security, Long lived access tokens.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => void handleDelete()}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? (
              <CircleNotch className="h-4 w-4 animate-spin" weight="light" />
            ) : (
              "Remove token"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
